import { Typography } from "@mui/material";
import { SettlementPaymentCell } from "Components/Users/UserListTable/SettlementPaymentCell";
import { UserRolesCell } from "Components/Users/UserListTable/UserRolesCell";
import { UserStatusCell } from "Components/Users/UserListTable/UserStatusCell";
import { VerificationCell } from "Components/Users/UserListTable/VerificationCell";
import {
  SettlementStatus,
  type UserDto,
  type Version,
} from "Infrastructure/Api/Api";
import { EXPIRATION_WARNING_DAYS } from "Utils/DocumentExpiration/documentExpirationUtils";
import { formatDate, getDaysUntilDate } from "Utils/DateUtils";
import { appUrl } from "Utils/UrlUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { getIdentityRoleFilterOptions } from "Utils/TableFilterUtils";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";
import {
  type MRT_ColumnDef,
  type MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Resources, useResource, type TFunction } from "Translations/Resources";
import { useMemo } from "react";

type UserWithLicensePaymentReference = UserDto & { paymentReference: string };

const createColumns = (
  t: TFunction,
): Array<MRT_ColumnDef<UserWithLicensePaymentReference>> => [
  {
    id: "CustomVerificationsBadge",
    header: t(Resources.Pages.Content.Users.TableHeaders.Verification),
    size: 0,
    Cell: ({ row }) => <VerificationCell user={row.original} />,
    filterVariant: "select",
    filterSelectOptions: [
      { label: t(Resources.Common.Verification.Verified), value: "Verified" },
      {
        label: t(Resources.Common.Verification.NotVerified),
        value: "NotVerified",
      },
    ],
    enableSorting: false,
  },
  {
    accessorKey: "party.clientIdentifier",
    header: t(Resources.Pages.Content.Users.TableHeaders.ClientIdentifier),
    size: 65,
    enableColumnFilter: false,
    enableSorting: false,
  },
  {
    accessorKey: "login",
    header: t(Resources.Pages.Content.Users.TableHeaders.Login),
    size: 150,
  },
  {
    accessorKey: "roleCode",
    header: t(Resources.Pages.Content.Users.TableHeaders.Role),
    Cell: ({ row }) => (
      <UserRolesCell
        roleCode={row.original.roleCode}
        identityRoles={row.original.identityRoles}
      />
    ),
    filterVariant: "multi-select",
    filterSelectOptions: getIdentityRoleFilterOptions(t),
    size: 190,
    // Multi-value cell — the legacy role column sort would not reflect what is displayed.
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: t(Resources.Pages.Content.Users.TableHeaders.Status),
    Cell: ({ row }) => <UserStatusCell status={row.original.status} />,
    filterVariant: "multi-select",
    filterSelectOptions: [
      { label: t(Resources.Form.UserStatus.Unknown), value: "Unknown" },
      {
        label: t(Resources.Form.UserStatus.InvitedWaitingForConfirmation),
        value: "InvitedWaitingForConfirmation",
      },
      { label: t(Resources.Form.UserStatus.Active), value: "Active" },
      {
        label: t(Resources.Form.UserStatus.WaitingForEmailConfirmation),
        value: "WaitingForEmailConfirmation",
      },
      { label: t(Resources.Form.UserStatus.Deactivated), value: "Deactivated" },
      {
        label: t(Resources.Form.UserStatus.BlockedFinances),
        value: "BlockedFinances",
      },
      { label: t(Resources.Form.UserStatus.Anonymous), value: "Anonymous" },
      { label: t(Resources.Form.UserStatus.Onboarding), value: "Onboarding" },
      {
        label: t(Resources.Form.UserStatus.EmailConfirmed),
        value: "EmailConfirmed",
      },
    ],
    size: 150,
  },
  {
    accessorKey: "party.firstName",
    header: t(Resources.Pages.Content.Users.TableHeaders.FirstName),
    accessorFn: ({ party }) => party.firstName || "",
    size: 130,
  },
  {
    accessorKey: "party.lastName",
    header: t(Resources.Pages.Content.Users.TableHeaders.LastName),
    accessorFn: ({ party }) => party.lastName || "",
    size: 130,
  },
  {
    accessorKey: "party.companyName",
    header: t(Resources.Pages.Content.Users.TableHeaders.Company),
    accessorFn: ({ party }) => party.companyName || "",
    size: 200,
  },
  {
    id: "dateCreated",
    accessorFn: ({ dateCreated }) => dateCreated,
    header: t(Resources.Pages.Content.Users.TableHeaders.DateCreated),
    size: 170,
    filterVariant: "date",
    Cell: ({ row }) => formatDate(row.original.dateCreated, true),
  },
  {
    accessorKey: "paymentReference",
    header: t(Resources.Pages.Content.Users.TableHeaders.PaymentReference),
    size: 180,
    enableColumnFilter: false,
    enableSorting: false,
  },
  {
    id: "documentExpiration",
    accessorFn: ({ party }) => {
      const primary = party.partyIdentification?.identificationValidTo;
      const secondary = party.partyIdentification?.secondIdentificationValidTo;
      const dates = [primary, secondary]
        .filter(Boolean)
        .map((d) => new Date(d!));
      if (dates.length === 0) {
        return null;
      }
      return dates.reduce((a, b) => (a < b ? a : b));
    },
    header: t(Resources.Pages.Content.Users.TableHeaders.DocumentExpiration),
    size: 170,
    filterVariant: "select",
    filterSelectOptions: [
      {
        label: t(Resources.Pages.Content.Users.Filters.ExpiringWithin30Days),
        value: "Expiring30",
      },
      {
        label: t(Resources.Pages.Content.Users.Filters.Expired),
        value: "Expired",
      },
    ],
    Cell: ({ cell }) => {
      const date = cell.getValue<Date | null>();
      if (!date) {
        return "-";
      }
      const days = getDaysUntilDate(date);
      const isExpired = days <= 0;
      const isExpiringSoon = days > 0 && days <= EXPIRATION_WARNING_DAYS;
      return (
        <Typography
          component="span"
          variant="body2"
          color={
            isExpired
              ? "error"
              : isExpiringSoon
                ? "warning.main"
                : "text.primary"
          }
          fontWeight={isExpired ? 600 : undefined}
        >
          {formatDate(date.toString(), true)}
        </Typography>
      );
    },
  },
  {
    accessorKey: "version",
    header: t(Resources.Pages.Content.Users.TableHeaders.Version),
    filterVariant: "select",
    filterSelectOptions: [
      { label: t(Resources.Form.Version.MooneLite), value: "MooneLite" },
      { label: t(Resources.Form.Version.FullMoone), value: "FullMoone" },
    ],
    size: 140,
    Cell: ({ cell }) => {
      const version = cell.getValue<Version | null>();
      return version ? t(Resources.Form.Version[version]) : "-";
    },
  },
  {
    accessorKey: "party.settlementStatus",
    header: t(Resources.Pages.Content.Users.TableHeaders.Settlement),
    size: 150,
    Cell: ({ row }) => <SettlementPaymentCell party={row.original.party} />,
    filterVariant: "select",
    filterSelectOptions: [
      {
        label: t(Resources.Components.Settlements.SettlementStatus.Paid),
        value: SettlementStatus.Paid,
      },
      {
        label: t(Resources.Components.Settlements.SettlementStatus.Unpaid),
        value: SettlementStatus.Unpaid,
      },
      {
        label: t(Resources.Components.Settlements.SettlementStatus.Overdue),
        value: SettlementStatus.Overdue,
      },
    ],
    enableSorting: false,
  },
  {
    accessorKey: "isAdmin",
    header: t(Resources.Pages.Content.Users.TableHeaders.Admin),
    filterVariant: "select",
    filterSelectOptions: [
      { label: t(Resources.Common.Yes), value: "true" },
      { label: t(Resources.Common.No), value: "false" },
    ],
    size: 100,
    Cell: ({ cell }) =>
      cell.getValue<boolean>()
        ? t(Resources.Common.Yes)
        : t(Resources.Common.No),
  },
];

type Props = {
  data: Array<UserWithLicensePaymentReference>;
  isRefetching?: boolean;
  isLoading?: boolean;
  rowCount: number;
  tableProps: Partial<MRT_TableOptions<UserWithLicensePaymentReference>>;
};

export const UserListTable = ({
  data,
  isRefetching,
  isLoading,
  rowCount,
  tableProps,
}: Props) => {
  const { t } = useResource();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();

  const COLUMNS = useMemo(() => createColumns(t), [t]);

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns: COLUMNS,
    rowCount,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    localization,
    state: {
      ...tableProps.state,
      isLoading,
      showProgressBars: isRefetching,
    },
    initialState: {
      ...tableProps.initialState,
      density: "compact",
    },
    muiTableBodyRowProps: ({ row }) => {
      const url = `${appUrl("user-detail")}/${row.original.userID}`;
      const onClick = createRowClickHandler(url);
      return {
        onClick,
        sx: { cursor: "pointer" },
      };
    },
    muiTablePaperProps: {
      sx: { borderRadius: "10px" },
    },
  });

  return <MaterialReactTable table={table} />;
};
